import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "profile-container" }
const _hoisted_2 = {
  key: 0,
  class: "loading"
}
const _hoisted_3 = {
  key: 1,
  class: "profile-content"
}
const _hoisted_4 = { class: "profile-header" }
const _hoisted_5 = { class: "user-basic-info" }
const _hoisted_6 = { class: "username" }
const _hoisted_7 = { class: "user-signature" }
const _hoisted_8 = { class: "profile-details" }
const _hoisted_9 = { class: "detail-section" }
const _hoisted_10 = { class: "detail-item" }
const _hoisted_11 = { class: "detail-item" }
const _hoisted_12 = { class: "detail-item" }
const _hoisted_13 = { class: "detail-item" }
const _hoisted_14 = { class: "detail-section" }
const _hoisted_15 = { class: "stats-grid" }
const _hoisted_16 = { class: "stat-number" }
const _hoisted_17 = { class: "stat-number" }
const _hoisted_18 = { class: "stat-item" }
const _hoisted_19 = { class: "stat-number" }
const _hoisted_20 = { class: "stat-item" }
const _hoisted_21 = { class: "stat-number" }
const _hoisted_22 = { class: "detail-section" }
const _hoisted_23 = { class: "detail-item" }
const _hoisted_24 = { class: "detail-item" }
const _hoisted_25 = { class: "profile-actions" }
const _hoisted_26 = {
  key: 2,
  class: "error"
}
const _hoisted_27 = { class: "dialog-footer" }

import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUserProfile, changePassword } from '@/user/api/userApi';
import { formatDate } from '@/utils/date';
import EditProfileModal from './EditProfileModal.vue'; // 导入编辑资料弹窗组件
import FollowingListModal from '../interaction/FollowingListModal.vue'; // 导入关注列表弹窗组件
import FollowerListModal from '../interaction/FollowerListModal.vue'; // 导入粉丝列表弹窗组件
import OtherUserProfileModal from './OtherUserProfileModal.vue'; // 导入其他用户资料弹窗组件
import ReportModal from '../report/ReportModal.vue'; // 导入举报弹窗组件

// --- API响应接口定义 ---
interface UserProfile {
  id: number;
  username: string;
  name: string;
  avatar: string;
  email?: string;
  phone?: string;
  signature?: string;
  status: number;
  roleName: string;
  createTime: string;
  likeCount: number;
  postCount: number;
  followerCount: number;
  followeeCount: number;
}

interface ApiResponse {
  code: number;
  msg: string | null;
  data: UserProfile;
}

// 修改密码表单数据
interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileView',
  setup(__props) {

const userProfile = ref<UserProfile | null>(null);
const loading = ref(true);
const router = useRouter();
const showPasswordDialog = ref(false);
const showEditProfileModal = ref(false);
const showFollowingListModal = ref(false); // 控制关注列表弹窗显示
const showFollowerListModal = ref(false); // 控制粉丝列表弹窗显示
const showOtherUserProfileModal = ref(false); // 控制其他用户资料弹窗显示
const selectedUserId = ref<number | null>(null); // 选中的用户ID
const reportModalRef = ref(); // 添加举报弹窗引用
const passwordForm = ref<ChangePasswordForm>({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
});

// 状态文本和样式（不再是计算属性，而是普通ref，在加载数据时更新）
const statusText = ref('');
const statusClass = ref('');

// 更新状态相关信息
const updateStatusInfo = (status: number) => {
  switch (status) {
    case 0:
      statusText.value = '封禁';
      statusClass.value = 'status-banned';
      break;
    case 1:
      statusText.value = '正常';
      statusClass.value = 'status-normal';
      break;
    case 2:
      statusText.value = '禁言';
      statusClass.value = 'status-muted';
      break;
    default:
      statusText.value = '未知';
      statusClass.value = 'status-unknown';
  }
};

const loadUserProfile = async () => {
  try {
    loading.value = true;
    
    // 调用真实的API获取用户资料
    const response = await getUserProfile();
    
    if (response.data && response.data.code === 1) {
      userProfile.value = response.data.data;
      // 更新状态信息
      updateStatusInfo(response.data.data.status);
    } else {
      throw new Error(response.data.msg || '获取用户资料失败');
    }
  } catch (error: any) {
    console.error('获取用户资料失败:', error);
    ElMessage.error(error.message || '获取用户资料失败');
  } finally {
    loading.value = false;
  }
};

// 显示修改密码对话框
const showChangePasswordDialog = () => {
  showPasswordDialog.value = true;
  // 重置表单
  passwordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
};

// 确认修改密码
const confirmChangePassword = async () => {
  if (!passwordForm.value.oldPassword) {
    ElMessage.error('请输入原密码');
    return;
  }
  
  if (!passwordForm.value.newPassword) {
    ElMessage.error('请输入新密码');
    return;
  }
  
  if (passwordForm.value.newPassword.length < 6) {
    ElMessage.error('新密码长度至少为6位');
    return;
  }
  
  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    ElMessage.error('两次输入的新密码不一致');
    return;
  }
  
  try {
    const response = await changePassword(
      passwordForm.value.oldPassword, 
      passwordForm.value.newPassword
    );
    
    if (response.data.code === 1) {
      ElMessage.success('密码修改成功');
      showPasswordDialog.value = false;
      // 清空表单
      passwordForm.value = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
    } else {
      ElMessage.error(response.data.msg || '密码修改失败');
    }
  } catch (error) {
    ElMessage.error('密码修改失败，请稍后重试');
    console.error('修改密码失败:', error);
  }
};

// 处理资料更新后的回调
const handleProfileUpdated = (updatedProfile: any) => {
  if (userProfile.value) {
    // 更新当前用户资料
    userProfile.value.name = updatedProfile.name;
    userProfile.value.email = updatedProfile.email;
    userProfile.value.phone = updatedProfile.phone;
    userProfile.value.signature = updatedProfile.signature;
    
    ElMessage.success('个人资料已更新');
  }
};

// 显示关注列表
const showFollowingList = () => {
  showFollowingListModal.value = true;
};

// 显示粉丝列表
const showFollowerList = () => {
  showFollowerListModal.value = true;
};

// 跳转到我的作品页面
const goToMyPosts = () => {
  router.push('/user/posts');
};

// 处理举报事件（来自子评论或用户）
const handleReportEvent = (payload: { targetType: number, targetId: number }) => {
  reportModalRef.value?.openReportDialog(payload.targetType, payload.targetId);
};

// 处理打开用户资料模态框事件
const handleOpenUserProfileModal = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (detail && detail.userId) {
    selectedUserId.value = detail.userId;
    showOtherUserProfileModal.value = true;
  }
};

onMounted(() => {
  loadUserProfile();
  // 添加事件监听器
  window.addEventListener('openUserProfileModal', handleOpenUserProfileModal);
});

onUnmounted(() => {
  // 移除事件监听器
  window.removeEventListener('openUserProfileModal', handleOpenUserProfileModal);
});

return (_ctx: any,_cache: any) => {
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, "加载中..."))
      : (userProfile.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_el_avatar, {
                size: 100,
                src: userProfile.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                class: "profile-avatar"
              }, null, 8, ["src"]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h2", _hoisted_6, _toDisplayString(userProfile.value.name || userProfile.value.username || '未知用户'), 1),
                _createElementVNode("p", _hoisted_7, _toDisplayString(userProfile.value.signature || '暂无签名'), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _cache[18] || (_cache[18] = _createElementVNode("h3", { class: "section-title" }, "账户信息", -1)),
                _createElementVNode("div", _hoisted_10, [
                  _cache[14] || (_cache[14] = _createElementVNode("label", null, "用户账号:", -1)),
                  _createElementVNode("span", null, _toDisplayString(userProfile.value.username || '未设置'), 1)
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", null, "用户邮箱:", -1)),
                  _createElementVNode("span", null, _toDisplayString(userProfile.value.email || '未填写'), 1)
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _cache[16] || (_cache[16] = _createElementVNode("label", null, "联系电话:", -1)),
                  _createElementVNode("span", null, _toDisplayString(userProfile.value.phone || '未填写'), 1)
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _cache[17] || (_cache[17] = _createElementVNode("label", null, "注册时间:", -1)),
                  _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(userProfile.value.createTime)), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _cache[23] || (_cache[23] = _createElementVNode("h3", { class: "section-title" }, "统计信息", -1)),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", {
                    class: "stat-item",
                    onClick: showFollowingList
                  }, [
                    _createElementVNode("div", _hoisted_16, _toDisplayString(userProfile.value.followeeCount || 0), 1),
                    _cache[19] || (_cache[19] = _createElementVNode("div", { class: "stat-label" }, "关注数", -1))
                  ]),
                  _createElementVNode("div", {
                    class: "stat-item",
                    onClick: showFollowerList
                  }, [
                    _createElementVNode("div", _hoisted_17, _toDisplayString(userProfile.value.followerCount || 0), 1),
                    _cache[20] || (_cache[20] = _createElementVNode("div", { class: "stat-label" }, "粉丝数", -1))
                  ]),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("div", _hoisted_19, _toDisplayString(userProfile.value.postCount || 0), 1),
                    _cache[21] || (_cache[21] = _createElementVNode("div", { class: "stat-label" }, "作品数", -1))
                  ]),
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("div", _hoisted_21, _toDisplayString(userProfile.value.likeCount || 0), 1),
                    _cache[22] || (_cache[22] = _createElementVNode("div", { class: "stat-label" }, "获赞数", -1))
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_22, [
                _cache[26] || (_cache[26] = _createElementVNode("h3", { class: "section-title" }, "账户状态", -1)),
                _createElementVNode("div", _hoisted_23, [
                  _cache[24] || (_cache[24] = _createElementVNode("label", null, "用户角色:", -1)),
                  _createElementVNode("span", null, _toDisplayString(userProfile.value.roleName || '普通用户'), 1)
                ]),
                _createElementVNode("div", _hoisted_24, [
                  _cache[25] || (_cache[25] = _createElementVNode("label", null, "账户状态:", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(statusClass.value)
                  }, _toDisplayString(statusText.value), 3)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_25, [
              _createVNode(_component_el_button, {
                type: "primary",
                round: "",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showEditProfileModal.value = true))
              }, {
                default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                  _createTextVNode("编辑资料", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_el_button, {
                type: "warning",
                round: "",
                onClick: showChangePasswordDialog
              }, {
                default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                  _createTextVNode("修改密码", -1)
                ]))]),
                _: 1
              })
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_26, "无法加载用户资料")),
    _createVNode(_component_el_dialog, {
      modelValue: showPasswordDialog.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showPasswordDialog).value = $event)),
      title: "修改密码",
      width: "400px",
      "close-on-click-modal": false,
      "before-close": () => { showPasswordDialog.value = false; }
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_27, [
          _createVNode(_component_el_button, {
            onClick: _cache[4] || (_cache[4] = ($event: any) => (showPasswordDialog.value = false))
          }, {
            default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: confirmChangePassword
          }, {
            default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
              _createTextVNode("确认修改", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          model: passwordForm.value,
          "label-width": "100px",
          style: {"padding":"10px 0"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, { label: "原密码" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: passwordForm.value.oldPassword,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((passwordForm.value.oldPassword) = $event)),
                  type: "password",
                  placeholder: "请输入原密码",
                  "show-password": ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { label: "新密码" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: passwordForm.value.newPassword,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((passwordForm.value.newPassword) = $event)),
                  type: "password",
                  placeholder: "请输入新密码",
                  "show-password": ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { label: "确认新密码" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: passwordForm.value.confirmPassword,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((passwordForm.value.confirmPassword) = $event)),
                  type: "password",
                  placeholder: "请再次输入新密码",
                  "show-password": ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "before-close"]),
    _createVNode(EditProfileModal, {
      modelValue: showEditProfileModal.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showEditProfileModal).value = $event)),
      onUpdated: handleProfileUpdated,
      onClose: _cache[7] || (_cache[7] = ($event: any) => (showEditProfileModal.value = false))
    }, null, 8, ["modelValue"]),
    _createVNode(FollowingListModal, {
      modelValue: showFollowingListModal.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showFollowingListModal).value = $event)),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (showFollowingListModal.value = false)),
      onReportUser: handleReportEvent
    }, null, 8, ["modelValue"]),
    _createVNode(FollowerListModal, {
      modelValue: showFollowerListModal.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showFollowerListModal).value = $event)),
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showFollowerListModal.value = false)),
      onReportUser: handleReportEvent
    }, null, 8, ["modelValue"]),
    _createVNode(OtherUserProfileModal, {
      modelValue: showOtherUserProfileModal.value,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showOtherUserProfileModal).value = $event)),
      userId: selectedUserId.value,
      onClose: _cache[13] || (_cache[13] = ($event: any) => (showOtherUserProfileModal.value = false))
    }, null, 8, ["modelValue", "userId"]),
    _createVNode(ReportModal, {
      ref_key: "reportModalRef",
      ref: reportModalRef
    }, null, 512)
  ]))
}
}

})